/* docTimelineAutomation.js — Iter-282
 *
 * Documents → Timeline automation engine (pure, deterministic).
 *
 * When a document request is fulfilled OR a recognised document
 * is uploaded, this module returns a side-effect plan describing:
 *
 *   1. The journey step that should advance (or `null` if the
 *      document carries no progression meaning).
 *   2. Any blockers that should be cleared (matched by step id
 *      OR by doc label similarity).
 *   3. The activity row to append.
 *
 * Why a pure module? So the wiring inside `chainStore.jsx`
 * remains thin (it just consumes the plan), and so the engine
 * is independently testable via `node --test`.
 *
 * Per the iter-282 brief:
 *   • Management Pack advances LEASEHOLD Mgmt Pack only —
 *     freehold deals never advance from a Management Pack
 *     upload.
 *   • Searches advance Searches.
 *   • Contract Pack advances Contract (`draft_contract` step
 *     on the journey vocabulary, which renders as the
 *     "Contract" node on the iter-277 rail).
 *   • Duplicate fulfilments / uploads MUST NOT duplicate
 *     activity rows. Idempotency is enforced by the caller
 *     (see `automationPlanFor` returning `null` when the step
 *     is already complete).
 *   • The engine never regresses a stage — only advances
 *     forward.
 */

/* ─── Mapping table ───────────────────────────────────────────────
 *
 * `template_id` → journey step id (chainStore vocabulary). The
 * journey step id is the same token consumed by
 * `markPilotJourneyStepComplete` → `STEP_ADVANCES_TO` map, so
 * each entry below produces a real stage advancement.
 *
 * `null` means: this document type carries no progression
 * meaning. The fulfilment is still logged but the timeline
 * stays put.
 */
export const DOC_TEMPLATE_TO_JOURNEY_STEP = Object.freeze({
  aml:                  "aml",
  memo_of_sale:         "offer",
  management_pack:      "mgmt_pack_received",   // leasehold only — see tenure guard
  contract_pack:        "draft_contract",        // "Contract" node on the rail
  draft_contract:       "draft_contract",
  searches:             "searches",
  survey_report:        "draft_contract",
  enquiries_responses:  "leasehold_enquiries",
  mortgage_offer:       "mortgage_offer",
  signed_contract:      "contracts",
  exchange_confirmation: "exchange",
  // explicit "no progression" entries — recorded for auditability
  // so reviewers know they were considered, not forgotten.
  other:                null,
  id_check:             null,
  bank_statement:       null,
});

/* ─── Steps that require a leasehold deal to fire ────────────── */
const LEASEHOLD_ONLY_STEPS = new Set([
  "mgmt_pack_requested",
  "mgmt_pack_received",
  "leasehold_enquiries",
]);

/* ─── Filename inference (heuristic) ─────────────────────────────
 *
 * When a document arrives without an explicit `template_id`
 * (e.g. an operator drag-drops "Management_Pack_v2.pdf"), we
 * sniff the filename for a recognised template token. Calm
 * regex set — no NLP, no guessing.
 *
 * Returns the matched `template_id` or `null`.
 */
const _FILENAME_PATTERNS = [
  [/manag(e)?ment[\s_-]*pack|mgmt[\s_-]*pack|mp[\s_-]?pack/i, "management_pack"],
  [/contract[\s_-]*pack/i,                                    "contract_pack"],
  [/signed[\s_-]*contract|exchange[\s_-]*ready/i,             "signed_contract"],
  [/draft[\s_-]*contract/i,                                   "draft_contract"],
  [/local[\s_-]*auth(ority)?[\s_-]*search|loc[\s_-]?search|searches?\b/i, "searches"],
  [/survey/i,                                                  "survey_report"],
  [/leasehold[\s_-]*enquir|enquir(y|ies)[\s_-]*response/i,    "enquiries_responses"],
  [/mortgage[\s_-]*offer|lender[\s_-]*offer/i,                "mortgage_offer"],
  [/memo(randum)?[\s_-]*of[\s_-]*sale|memo[\s_-]*sale/i,      "memo_of_sale"],
  [/aml|kyc|source[\s_-]*of[\s_-]*funds/i,                    "aml"],
  [/exchange[\s_-]*confirm/i,                                 "exchange_confirmation"],
];

export function inferTemplateFromDocName(name) {
  if (!name) return null;
  const s = String(name);
  for (const [rx, tpl] of _FILENAME_PATTERNS) {
    if (rx.test(s)) return tpl;
  }
  return null;
}

/* ─── Doc → journey step resolver ────────────────────────────── */
export function mapDocToJourneyStep(template_id, doc_label) {
  const direct = template_id
    ? DOC_TEMPLATE_TO_JOURNEY_STEP[template_id]
    : undefined;
  if (direct !== undefined) return direct;
  // Fallback: try inferring from the label if the template wasn't
  // a known key.
  const inferred = inferTemplateFromDocName(doc_label || template_id);
  return inferred ? DOC_TEMPLATE_TO_JOURNEY_STEP[inferred] || null : null;
}

/* ─── Blocker matcher ─────────────────────────────────────────── */
function _blockerMatches(blocker, stepId, docLabel) {
  if (!blocker) return false;
  if (blocker.status === "resolved") return false;
  const tagged = blocker.step_id || blocker.journey_step || blocker.target_step;
  if (tagged && tagged === stepId) return true;
  // Label-similarity fallback. The seeded blocker copy mentions
  // the document name ("Awaiting management pack",
  // "Searches outstanding") so a calm token-overlap check is
  // enough for v1.
  if (!docLabel) return false;
  const bLabel = String(blocker.title || blocker.label || blocker.description || "").toLowerCase();
  const dLabel = String(docLabel).toLowerCase();
  if (!bLabel || !dLabel) return false;
  // Token overlap (≥ 1 shared 4+ char token).
  const bTokens = new Set(bLabel.split(/[^a-z]+/).filter((t) => t.length >= 4));
  const dTokens = dLabel.split(/[^a-z]+/).filter((t) => t.length >= 4);
  return dTokens.some((t) => bTokens.has(t));
}

/* ─── Plan builder ────────────────────────────────────────────────
 *
 * Returns `null` when there's nothing to do (idempotent path),
 * or an object describing the side effects.
 */
export function automationPlanFor({ deal, doc, request } = {}) {
  if (!deal) return null;
  const template_id = (doc && doc.template_id)
                   || (request && request.template_id)
                   || null;
  const doc_label   = (doc && (doc.doc_label || doc.label || doc.name))
                   || (request && (request.doc_label || request.label || request.title))
                   || null;

  // 1) Resolve the journey step.
  let stepId = mapDocToJourneyStep(template_id, doc_label);
  if (!stepId && (doc && doc.name)) {
    const inferred = inferTemplateFromDocName(doc.name);
    if (inferred) stepId = DOC_TEMPLATE_TO_JOURNEY_STEP[inferred] || null;
  }

  // 2) Tenure guard — leasehold-only steps never fire on
  //    freehold deals.
  const tenure = String(deal.tenure || "").toLowerCase();
  if (stepId && LEASEHOLD_ONLY_STEPS.has(stepId) && tenure !== "leasehold") {
    stepId = null;
  }

  // 3) Idempotency — never advance a step already complete.
  const completed = Array.isArray(deal.completedSteps) ? deal.completedSteps : [];
  const alreadyComplete = stepId && completed.includes(stepId);
  if (alreadyComplete) stepId = null;

  // 4) Blocker matching.
  const blockers = Array.isArray(deal.blockers) ? deal.blockers : [];
  const blockersToClear = blockers
    .filter((b) => _blockerMatches(b, stepId, doc_label))
    .map((b) => b.id)
    .filter(Boolean);

  // 5) Compose activity row (only when we have something to log).
  const sourceLabel = doc_label || "document";
  const activityRow = stepId || blockersToClear.length > 0
    ? {
        id:         `auto-${deal.id}-${stepId || "doc"}-${Date.now()}`,
        type:       "automation",
        kind:       "automation",
        label:      stepId
          ? `Auto-progressed: ${_humanStep(stepId)} from ${sourceLabel}`
          : `Blocker cleared from ${sourceLabel}`,
        title:      stepId
          ? `${_humanStep(stepId)} auto-progressed`
          : "Blocker cleared",
        actor_role: "Automation",
        created_at: new Date().toISOString(),
        source:     "doc-timeline-automation",
        templateId: template_id || null,
      }
    : null;

  if (!stepId && blockersToClear.length === 0) return null;

  return {
    stepToMarkComplete: stepId,           // may be null when only blockers clear
    blockersToClear,                      // string[] of blocker ids
    activityRow,
    templateId: template_id,
    docLabel:   doc_label,
  };
}

/* ─── Human-readable step label (for activity copy) ─────────── */
function _humanStep(stepId) {
  return ({
    offer:                "Offer Agreed",
    aml:                  "AML Checks",
    draft_contract:       "Contract",
    mgmt_pack_requested:  "Management Pack Requested",
    mgmt_pack_received:   "Management Pack Received",
    searches:             "Searches",
    mortgage_offer:       "Mortgage Offer",
    leasehold_enquiries:  "Leasehold Enquiries",
    contracts:            "Contracts",
    exchange:             "Exchange",
    completion:           "Completion",
  })[stepId] || String(stepId).replace(/_/g, " ");
}

/* ─── Feature-flag helper ──────────────────────────────────────── */
export function isDocTimelineAutomationEnabled() {
  return String(
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_DOC_TIMELINE_AUTOMATION_V1)
      || ""
  ).toLowerCase() === "true";
}
